// Types for the diff output
type HighlightType = "updated" | "added" | "none";

interface DiffValue<T> {
  value: T;
  highlight: HighlightType;
}

type DiffResult<T> = {
  [P in keyof T]: T[P] extends Array<infer U>
    ? Array<DiffResult<U>>
    : T[P] extends object
      ? DiffResult<T[P]>
      : DiffValue<T[P]>;
};

// shared by Parts and Tools
interface BaseItem {
  mpim: string;
  name: string;
  quantity: number;
  value: number;
}

// Tools include `cost` in addition to BaseItem fields
interface Tool extends BaseItem {
  cost: number;
}

// Union of Part or Tool (safe for comparison use)
type ItemLike = BaseItem | Tool;

// Fully typed compareObjects
function compareObjects<T extends object>(oldObj: T, newObj: T): DiffResult<T> {
  const result = {} as DiffResult<T>;

  // Helper function to check if values are equal
  const isEqual = (val1: unknown, val2: unknown): boolean => {
    if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) return false;
      return val1.every((item, index) => isEqual(item, val2[index]));
    }
    if (typeof val1 === "object" && typeof val2 === "object" && val1 !== null && val2 !== null) {
      return JSON.stringify(val1) === JSON.stringify(val2);
    }
    return val1 === val2;
  };

  for (const key in newObj) {
    if (Object.prototype.hasOwnProperty.call(newObj, key)) {
      const oldValue = oldObj[key];
      const newValue = newObj[key];

      // Handle arrays
      if (Array.isArray(newValue)) {
        result[key] = newValue.map((item, index) => {
          const oldItem = Array.isArray(oldValue) ? oldValue[index] : undefined;
          if (typeof item === "object" && item !== null) {
            return compareObjects(oldItem || ({} as T), item);
          }
          return {
            value: item,
            highlight: !oldItem ? "added" : !isEqual(oldItem, item) ? "updated" : "none",
          };
        }) as DiffResult<T>[typeof key];
      } else if (typeof newValue === "object" && newValue !== null) {
        // Handle nested objects
        result[key] = compareObjects((oldValue as T) || ({} as T), newValue as T) as DiffResult<T>[typeof key];
      } else {
        // Handle primitive values
        result[key] = {
          value: newValue,
          highlight: !oldValue ? "added" : !isEqual(oldValue, newValue) ? "updated" : "none",
        } as DiffResult<T>[typeof key];
      }
    }
  }

  return result;
}

export { compareObjects, type DiffResult, type DiffValue, type HighlightType, type BaseItem, type Tool, type ItemLike };
